import { unwrapApiPayload } from "@/utils/graphql";
import { getApiMessage, isApiSuccess } from "@/mixins/Extras/ApiResponse";
import { parseApiBoolean, formatUserContactDisplay, formatPhoneNumber } from "@/utils/format";
import { parseChatbotDetails } from "@/utils/agentOtherSettings";

const ORG_FIELDS = [
  "org_name",
  "org_tag_line",
  "org_description",
  "org_contact_number",
  "org_contact_name",
  "org_country_code",
  "org_address",
  "org_city",
  "org_state",
  "org_pincode",
  "org_knowledeg_base",
  "sip_number",
];

const ORG_META_FIELDS = [
  "org_status",
  "org_contact_email",
  "org_full_number",
  "last_updated_on",
  "created_on",
  "knowledge_base_synced_on",
  "org_knowledeg_base_updated",
  "org_logo_s3_key",
  "org_knowledge_base_s3_keys",
  "kb_s3_prefix",
  "internal_org_id",
  "org_website_url",
  "knowledge_base_id",
  "default_incoming_task_id",
  "chatbot_details",
  "restricted_caller_audio_text",
  "text_audio_url",
  "ai_engine",
  "api_key",
  "oauth_client_id",
  "oauth_client_secret",
  "oauth_token_url",
  "client_id",
  "client_secret",
  "token_url",
  "greeting_message",
  "system_prompt",
  "disclaimer_text",
  "business_hours_start",
  "business_hours_end",
  "out_of_hours_message",
  "email_addresses",
  "whatsapp_numbers",
  "business_query_email_addresses",
  "business_query_whatsapp_numbers",
  "default_field_definitions",
  "default_lead_statuses",
  "dashboard_digest_weekly_day",
  "dashboard_digest_weekly_time",
  "dashboard_digest_monthly_day",
  "dashboard_digest_monthly_time",
];

const ORG_NUMERIC_FIELDS = [
  "credits_balance",
  "total_number_of_calls",
  "daily_rental",
  "voice_agent_cost_per_minute",
];

const ORG_BOOLEAN_FIELDS = [
  "send_call_logs_by_email",
  "send_call_logs_by_whatsapp",
  "send_business_queries_by_email",
  "send_business_queries_by_whatsapp",
  "restrict_incoming_calls",
  "outgoing_enabled",
  "disclaimer_enabled",
  "send_dashboard_digest_by_email",
  "send_dashboard_digest_by_whatsapp",
  "dashboard_digest_cc_agents",
  "dashboard_digest_weekly_enabled",
  "dashboard_digest_monthly_enabled",
];

function mapOrganizationFields(org) {
  if (!org || typeof org !== "object") {
    return {};
  }

  const organization = {};
  for (const field of ORG_FIELDS) {
    if (org[field] != null) {
      organization[field] = String(org[field]);
    }
  }

  const sip = org.assigned_sip_number ?? org.sip_number;
  if (sip != null && !organization.sip_number) {
    organization.sip_number = String(sip);
  }

  for (const field of ORG_META_FIELDS) {
    if (org[field] != null) {
      organization[field] = org[field];
    }
  }

  for (const field of ORG_NUMERIC_FIELDS) {
    if (org[field] != null && org[field] !== "") {
      const num = Number(org[field]);
      organization[field] = Number.isNaN(num) ? org[field] : num;
    }
  }

  for (const field of ORG_BOOLEAN_FIELDS) {
    if (org[field] != null && org[field] !== "") {
      organization[field] = parseApiBoolean(org[field], false);
    } else if (typeof org[field] === "boolean") {
      organization[field] = org[field];
    }
  }

  if (org.org_knowledge_base_s3_keys != null) {
    const kbKeys = org.org_knowledge_base_s3_keys;
    organization.org_knowledge_base_s3_keys = Array.isArray(kbKeys)
      ? kbKeys
      : [kbKeys].filter(Boolean);
  }

  for (const field of [
    "email_addresses",
    "whatsapp_numbers",
    "business_query_email_addresses",
    "business_query_whatsapp_numbers",
    "dashboard_digest_email_addresses",
    "dashboard_digest_whatsapp_numbers",
    "dashboard_digest_times",
  ]) {
    if (org[field] != null) {
      organization[field] = Array.isArray(org[field])
        ? org[field].map((item) => String(item ?? "").trim()).filter(Boolean)
        : [];
    }
  }

  const orgId = org.org_id ?? org.orgId;
  if (orgId != null) {
    organization.org_id = String(orgId);
  }

  if (!organization.org_logo_s3_key) {
    const logoKey =
      org.org_logo_s3_key ?? org.org_logo ?? org.logo_s3_key ?? org.logo_s3key;
    if (logoKey != null && String(logoKey).trim()) {
      organization.org_logo_s3_key = String(logoKey).trim();
    }
  }

  if (org.chatbot_details != null) {
    organization.chatbot_details = parseChatbotDetails(org.chatbot_details);
  }

  return organization;
}

/**
 * Normalizes getOrgDetails GraphQL response into organisation fields.
 */
export function normalizeOrgDetailsPayload(raw) {
  const record = unwrapApiPayload(raw);
  if (!record || typeof record !== "object") {
    return null;
  }

  const org =
    record.orgDetails ??
    record.org_details ??
    record.organization ??
    record.org ??
    record;

  if (!org || typeof org !== "object") {
    return null;
  }

  return mapOrganizationFields(org);
}

/**
 * Normalizes getOrgDetailsForLandingPage REST response.
 * URL slug is internal_org_id; API body field remains org_id with that slug value.
 */
export function normalizeLandingOrgPayload(raw, requestedSlug) {
  if (!isApiSuccess(raw)) {
    return { ok: false, error: getApiMessage(raw, "Organisation not found.") };
  }

  let item;
  try {
    item = unwrapApiPayload(raw);
  } catch (err) {
    return {
      ok: false,
      error: err.message || "Organisation not found.",
    };
  }

  if (!item || typeof item !== "object") {
    return { ok: false, error: "Organisation not found." };
  }

  const slug = String(item.internal_org_id ?? "")
    .trim()
    .toLowerCase();
  const expected = String(requestedSlug ?? "")
    .trim()
    .toLowerCase();
  if (expected && slug && slug !== expected) {
    return { ok: false, error: "Organisation not found." };
  }

  const status = String(item.org_status ?? "")
    .trim()
    .toUpperCase();
  if (status && status !== "ACTIVE") {
    return { ok: false, error: "This organisation is not available." };
  }

  return {
    ok: true,
    orgSlug: slug || expected,
    organization: mapOrganizationFields(item),
  };
}

export function normalizeSessionPayload(raw) {
  const record = unwrapApiPayload(raw);
  if (!record || typeof record !== "object") {
    return null;
  }

  let user = record;
  let org = {};

  if (record.userDetails || record.orgDetails) {
    user = record.userDetails ?? {};
    org = record.orgDetails ?? {};
  } else {
    const nested =
      record.organization ??
      record.org ??
      record.org_details ??
      record.orgDetails ??
      {};
    org = { ...nested, ...record };
  }

  return {
    currentUserId:
      user.user_id ?? user.current_user_id ?? user.currentUserId ?? null,
    orgId: user.org_id ?? org.org_id ?? user.orgId ?? org.orgId ?? null,
    userEmail: user.user_email_id ?? user.userEmail ?? user.email ?? null,
    emailAddress: user.email_address ?? user.emailAddress ?? null,
    emailChannel: user.email_channel ?? user.emailChannel ?? null,
    userName: user.user_name ?? user.userName ?? user.name ?? null,
    userRole: user.user_role ?? user.userRole ?? user.role ?? null,
    agentType: user.agent_type ?? user.agentType ?? null,
    organization: mapOrganizationFields(org),
  };
}

export function normalizeUserRecord(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  return {
    id: item.user_id ?? item.id ?? item.userId,
    name: item.user_name ?? item.name ?? "—",
    email: item.user_email_id ?? item.email ?? "—",
    emailInbox: item.email_address ?? item.emailInbox ?? null,
    role: item.user_role ?? item.role ?? null,
    status: item.user_status ?? item.status ?? null,
    countryCode: item.country_code ?? item.countryCode ?? null,
    mobileNumber: item.mobile_number ?? item.mobileNumber ?? null,
    // Call-transfer target eligibility (Campaign Settings "Transfer calls
    // to") -- a person is only offered as a softphone target when they
    // actually have an extension provisioned.
    softphoneExtension: item.softphone_extension ?? item.softphoneExtension ?? null,
    contactDisplay: formatUserContactDisplay(
      item.country_code ?? item.countryCode,
      item.mobile_number ?? item.mobileNumber,
    ),
    recoveryEmail: item.recovery_email ?? item.recoveryEmail ?? null,
    recoveryWhatsapp: item.recovery_whatsapp ?? item.recoveryWhatsapp ?? null,
    agentType: item.agent_type ?? item.agentType ?? null,
    agentGender: item.agent_gender ?? item.agentGender ?? null,
    agentLanguage: item.agent_language ?? item.agentLanguage ?? null,
    isOnboardingDefault: Boolean(item.is_onboarding_default ?? item.isOnboardingDefault),
    createdOn: item.created_on ?? item.createdOn ?? null,
    createdBy: item.created_by ?? item.createdBy ?? null,
  };
}

export function normalizeTransactionRecord(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const id =
    item.transaction_id ??
    item.org_transaction_id ??
    item.id ??
    item.uuid ??
    null;

  return {
    id: id != null ? String(id) : null,
    transactionType:
      item.transaction_type ?? item.transactionType ?? item.type ?? null,
    deductionType:
      item.deduction_type ?? item.deductionType ?? item.category ?? null,
    amount:
      item.amount ??
      item.credits ??
      item.credit_amount ??
      item.transaction_amount ??
      item.value ??
      null,
    duration_seconds: item.duration_seconds,
    balanceBefore:
      item.credits_balance_before ??
      item.balance_before ??
      item.balanceBefore ??
      null,
    balanceAfter:
      item.credits_balance_after ??
      item.balance_after ??
      item.balanceAfter ??
      null,
    description:
      item.description ?? item.reason ?? item.notes ?? item.remarks ?? null,
    createdOn:
      item.created_on ??
      item.createdOn ??
      item.transaction_date ??
      item.timestamp ??
      null,
    callId: item.call_id ?? item.call_uuid ?? item.callId ?? null,
  };
}

function pickCreditRequestCredits(item) {
  const direct =
    item.credits ??
    item.no_of_credits ??
    item.number_of_credits ??
    item.credits_requested ??
    item.requested_credits ??
    item.credit_amount ??
    item.credit_count ??
    item.Credits ??
    item.NoOfCredits ??
    item.amount;

  if (direct != null && direct !== "") return direct;

  const key = Object.keys(item).find((k) => {
    const normalized = k.toLowerCase().replace(/_/g, "");
    return (
      normalized === "credits" ||
      normalized === "noofcredits" ||
      normalized === "numberofcredits" ||
      normalized === "creditsrequested" ||
      normalized === "requestedcredits"
    );
  });

  return key ? item[key] : null;
}

function pickCreditRequestEmail(item) {
  const direct =
    item.user_email_id ??
    item.requested_by_email ??
    item.requester_email ??
    item.requested_by_email_id ??
    item.email ??
    item.user_email ??
    item.requested_by ??
    item.requestedBy;

  if (direct != null && String(direct).trim()) return String(direct).trim();

  const key = Object.keys(item).find((k) => {
    const normalized = k.toLowerCase();
    return normalized.includes("email") || normalized === "requested_by";
  });

  const value = key ? item[key] : null;
  return value != null && String(value).trim() ? String(value).trim() : null;
}

export function normalizeCreditRequestRecord(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const id =
    item.credit_request_id ?? item.request_id ?? item.id ?? item.uuid ?? null;

  return {
    id: id != null ? String(id) : null,
    credits: pickCreditRequestCredits(item),
    comments:
      item.comments ?? item.comment ?? item.reason ?? item.notes ?? null,
    workflowStatus:
      item.workflow_status ?? item.workflowStatus ?? item.status ?? null,
    createdOn:
      item.created_on ??
      item.createdOn ??
      item.requested_on ??
      item.requested_at ??
      null,
    updatedOn: item.updated_on ?? item.updatedOn ?? null,
    requestedByEmail: pickCreditRequestEmail(item),
  };
}

export function normalizeReachoutRecord(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const id =
    item.contact_us_id ?? item.reachout_id ?? item.id ?? item.uuid ?? null;

  const phoneRaw =
    item.phone ??
    item.contact_number ??
    item.mobile ??
    item.phone_number ??
    null

  return {
    id: id != null ? String(id) : null,
    name: item.name ?? item.user_name ?? item.contact_name ?? "—",
    email: item.email ?? item.contact_email ?? item.user_email ?? "—",
    phone: phoneRaw ? formatPhoneNumber(phoneRaw) : null,
    comments: item.comments ?? "—",
    status: item.status ?? item.contact_status ?? null,
    createdOn: item.created_on ?? item.createdOn ?? null,
  };
}
